import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { Seo } from "@/lib/seo";
import { PageHero } from "@/components/page-hero";
import { CtaBlock } from "@/components/cta";
import { Reveal } from "@/lib/motion";
import { momentoBySlug, MOMENTOS } from "@/content/moments";
import { servicioBySlug, SERVICIOS } from "@/content/services";

export default function MomentoDetalle() {
  const { slug } = useParams();
  const momento = momentoBySlug(slug);

  if (!momento) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center sm:px-8">
        <p className="eyebrow mb-4">Momento no encontrado</p>
        <h1 className="font-serif text-4xl text-ink-deep">Ese momento no lo tenemos cubierto</h1>
        <Link to="/momentos" data-testid="momento-volver-index" className="mt-6 inline-flex text-azul underline underline-offset-4">
          Ver todos los momentos
        </Link>
      </div>
    );
  }

  const servicios = momento.serviciosRelacionados
    .map((s) => servicioBySlug(s))
    .filter(Boolean) as typeof SERVICIOS;
  const idx = MOMENTOS.findIndex((m) => m.slug === momento.slug);
  const siguiente = MOMENTOS[(idx + 1) % MOMENTOS.length];

  return (
    <>
      <Seo title={momento.seoTitle} description={momento.seoDesc} path={`/momentos/${momento.slug}`} />
      <PageHero eyebrow="Momento de vida" titulo={momento.nombre} lead={momento.intro} />

      <section className="bg-parchment">
        <div className="mx-auto grid max-w-7xl gap-16 px-4 py-16 sm:px-8 sm:py-24 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <h2 className="font-serif text-2xl text-ink-deep sm:text-3xl">¿Te suena familiar?</h2>
              <ul className="mt-8">
                {momento.situaciones.map((s, i) => (
                  <li
                    key={i}
                    data-testid={`momento-situacion-${i}`}
                    className="flex items-start gap-4 border-t border-hairline py-4 text-ink-text last:border-b"
                  >
                    <span className="mt-1 font-mono text-xs text-azul">{String(i + 1).padStart(2, "0")}</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal className="mt-16">
              <h2 className="font-serif text-2xl text-ink-deep sm:text-3xl">Cómo te acompañamos aquí</h2>
              <div className="mt-8 space-y-6">
                {momento.comoAyudamos.map((c, i) => (
                  <div key={i} className="flex gap-4" data-testid={`momento-ayuda-${i}`}>
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-azul" />
                    <p className="text-lg leading-relaxed text-ink-text">{c}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <aside className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="border border-hairline bg-parchment-surface/60 p-8" data-testid="momento-servicios-relacionados">
                <p className="eyebrow mb-6">Servicios que aplican aquí</p>
                <div className="space-y-4">
                  {servicios.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/servicios/${s.slug}`}
                      data-testid={`momento-servicio-link-${s.slug}`}
                      className="group block border border-hairline bg-white p-6 transition-colors hover:border-azul/50"
                    >
                      <h3 className="font-serif text-xl text-ink-deep">{s.nombre}</h3>
                      <p className="mt-1.5 text-sm text-ink-muted">{s.frase}</p>
                      <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-azul">
                        Conocer el servicio
                        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                      </span>
                    </Link>
                  ))}
                </div>
                <Link
                  to="/orientador"
                  data-testid="momento-orientador-link"
                  className="mt-8 flex items-center justify-between border-l-2 border-azul bg-white p-5 text-sm text-ink-text transition-colors hover:bg-azul-light/40"
                >
                  ¿No es exactamente esto? Usa el orientador
                  <ArrowRight className="h-4 w-4 text-azul" />
                </Link>
              </div>

              <Link
                to={`/momentos/${siguiente.slug}`}
                data-testid="momento-siguiente-link"
                className="group mt-6 flex items-center justify-between border border-hairline p-5 text-sm text-ink-muted transition-colors hover:text-ink-deep"
              >
                <span className="inline-flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4 text-azul" />
                  Siguiente momento: {siguiente.nombre}
                </span>
                <ArrowRight className="h-4 w-4 text-azul" />
              </Link>
            </Reveal>
          </aside>
        </div>
      </section>

      <CtaBlock
        titulo={`¿Es tu momento? Cuéntanos.`}
        sub={`Estás en "${momento.nombre}". Describe tu situación y te decimos cómo seguir, en palabras claras.`}
      />
    </>
  );
}
